import React from "react";
import { Link } from "react-router-dom";
import { Box, Switch, Tab, TabProps, TextField, styled } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import * as Yup from "yup";

import EditImg from "../components/images/edit.png";
import { formatPhone } from "./utils";
import type { FieldRule } from "../types";

export const fieldRules: Record<string, FieldRule> = {
    paypal: {
        title: `Paypal`,
        fields: [{ l: `PayPal Email Address`, v: `paypal-email`, t: `email` }],
        validator: Yup.object().shape({
            "paypal-email": Yup.string().email(`Please enter a valid email`).max(255).required(`Email is required`),
        }),
        updateWithSettings: true,
    },
    venmo: {
        title: `Venmo`,
        fields: [{ l: `Venmo Phone Number`, v: `venmo-phone`, regex: /[^.0-9]/g, visualFilter: formatPhone }],
        validator: Yup.object().shape({
            "venmo-phone": Yup.string()
                .min(10, `Please enter full phone number`)
                .max(11, `Please enter valid phone number`)
                .required(`Phone number is required`),
        }),
        updateWithSettings: true,
    },
    name: {
        title: `Update Your Name`,
        fields: [
            { l: `First Name`, v: `firstName`, htmlName: `firstName` },
            { l: `Last Name`, v: `lastName`, htmlName: `lastName` },
        ],
        validator: Yup.object().shape({
            firstName: Yup.string().required(`Please enter a first name`).min(2, `Please enter a first name`),
            lastName: Yup.string().required(`Please enter a last name`).min(2, `Please enter a last name`),
        }),
    },
    address: {
        title: `Update Your Street Address`,
        fields: [
            { l: `Street Address`, v: `address1`, htmlName: `address`, id: `lookupaddress` },
            { l: `Unit/Apartment/Building`, v: `address2`, htmlName: `address2`, id: `lookupaddress2` },
            { l: `City`, v: `city`, id: `city`, htmlName: `city`, width: 350, mr: 2 },
            { l: `State`, v: `state`, id: `state`, htmlName: `state`, width: 75, mr: 2 },
            { l: `Zip`, v: `zipcode`, id: `zipcode`, htmlName: `zipcode`, width: 125 },
        ],
        validator: Yup.object().shape({
            city: Yup.string().required(`Please select your address`),
            state: Yup.string().required(`Please select your address`),
            zipcode: Yup.string().required(`Please select your address`),
            address1: Yup.string().required().min(12, `Please enter your address`),
        }),
    },
};

export const missingTransactionFieldRules = {
    fields: [
        { l: `Order ID`, v: `orderId`, htmlName: `orderId`, id: `orderId` },
        { l: `Full Name Used`, v: `fullName`, htmlName: `fullName`, id: `fullName` },
        { l: `Email Used`, v: `email`, id: `email`, htmlName: `city` },
        {
            l: `Subtotal (before tax and shipping)`,
            v: `subTotal`,
            id: `subTotal`,
            regex: /[^.0-9]/g,
            htmlName: `subTotal`,
            pre: `$`,
        },
    ],
    validator: Yup.object().shape({
        orderId: Yup.string().required(`Please enter the Order ID`),
        fullName: Yup.string().required(`Please enter the full name associated with the order`),
        email: Yup.string()
            .matches(/^\S+@\S+\.\S+$/, `Please enter a valid email address`)
            .required(`Please enter the email address associated with the order`),
        subTotal: Yup.string()
            .matches(/^\$?\d+(,\d{3})*\.?[0-9]?[0-9]?$/, `Please enter a valid amount`)
            .required(`Please enter the order subtotal`),
    }),
};

export const cashbackFieldRules = {
    fields: [
        { l: `Cash Back Amount`, v: `cashback`, htmlName: `cashback`, id: `cashback` },
        { l: `Cash Back Method`, v: `method`, htmlName: `method`, id: `method` },
        { l: `PayPal Email`, v: `email`, htmlName: `email`, id: `email` },
        { l: `Venmo Phone`, v: `phone`, id: `phone`, regex: /[^.0-9]/g, htmlName: `phone` },
    ],
    validator: Yup.object().shape({
        cashback: Yup.string().typeError(`Please enter a valid amwount`).required(`Please enter a valid amount`),
        method: Yup.string().oneOf([`PayPal`, `Venmo`]),
        email: Yup.string().when(`method`, {
            is: `PayPal`,
            then: () =>
                Yup.string().email(`Please enter a valid email address`).required(`Please enter a valid email address`),
        }),
        phone: Yup.string().when(`method`, {
            is: `Venmo`,
            then: () =>
                Yup.string()
                    .transform(v => v.replace(/[^0-9]/g, ``))
                    .min(10)
                    .max(11)
                    .required(`Please enter a valid phone number`),
        }),
    }),
};

export const LiLink = styled(Link)<{ nohover?: boolean; nopadding?: boolean; variant?: string; py?: number }>(
    ({ color, theme, sx = {}, nohover, nopadding }) => ({
        textDecoration: `none`,
        padding: nopadding ? `0` : `5px 0`,
        color: color || (sx as Record<string, string>)?.color || theme.palette.account?.accent || `black`,
        "&.fullheight": {
            height: `100%`,
            display: `inline-block`,
        },

        "&.dblock": {
            display: `block`,
        },
        "&:visited": {
            color: color || (sx as Record<string, string>)?.color || theme.palette.account?.accent || `black`,
        },

        "&:hover": {
            color: nohover ? (color ?? `inherit`) : `#303030`,
        },

        "&.bghover": {
            padding: 0,
            paddingLeft: `16px`,
            "&:hover": {
                color: `#303030`,
                background: `#eee`,
            },
        },
        ...(sx as Record<string, string>),
    })
);

export const AppWrap = styled(Box)(() => ({
    maxWidth: `1440px`,
    margin: `0 auto`,
    minHeight: `780px`,
}));

export const GreenSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: theme.palette.account.accent,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: `${theme.palette.account.accent}80`,
    },
}));

export const LiTab = ({
    children,
    label,
    sx,
    ...rest
}: { children?: React.ReactNode; label: string; sx?: SxProps<Theme> } & Omit<TabProps, "label" | "sx">) => (
    <Tab
        label={label}
        sx={{
            color: `#333`,
            "&.Mui-selected": {
                color: `#333`,
            },
            padding: `12px 0`,
            margin: `0 8px`,
            minWidth: `unset`,
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Tab>
);

export const GridArea = styled(Box)<{ storeCellsAcross?: number; contentAreaWidth?: number }>(
    ({ storeCellsAcross = 0, contentAreaWidth, sx }) => ({
        padding: `0 20px`,
        display: `grid`,
        gap: `20px 25px`,
        gridTemplateRows: 166,
        gridTemplateColumns: Array(storeCellsAcross).fill(`1fr`).join(` `),
        width: `100%`,
        margin: `0 auto`,
        maxWidth: contentAreaWidth,
        ...(sx as Record<string, string | number>),
    })
);

export const ContentArea = styled(`div`)<{ size?: string; name?: string }>(({ size }) => ({
    width: "100%",
    padding: "0 20px",
    display: "flex",
    justifyContent: "space-around",
    flexWrap: "nowrap",

    "&.extramargin": {
        "& > div:first-child": {
            marginLeft: 20,
        },
        "& > div:last-child": {
            marginRight: 20,
        },
    },

    "&.scroll": {
        overflowX: size === "small" ? "auto" : "hidden",
    },

    "&.mtop": {
        marginTop: 40,
    },

    "&.nopad": {
        padding: 0,
    },

    "&.marginTop": {
        "& button": {
            marginTop: 80,
        },
    },

    "&.between": {
        justifyContent: "space-between",
    },

    "&.nowrap": {
        whiteSpace: "nowrap",

        "& div": {
            whiteSpace: "initial",
        },
    },

    "&.noflex": {
        display: "block",
    },

    "&.twoup": {
        display: "inline-block",
    },

    "&.left": {
        textAlign: "left",
        justifyContent: "flex-start",
    },

    "&.leftj": {
        justifyContent: "flex-start",
    },

    "&.right": {
        width: 300,
        margin: "0 0 0 25px",
        display: "inline-block",
        verticalAlign: "top",
    },

    "&.wrap": {
        flexWrap: "wrap",

        "&.center": {
            alignItems: "center",
        },
    },

    "&.center": {
        justifyContent: "center",
    },

    "&.relative": {
        position: "relative",
    },
}));

export const Button = styled(`button`)({
    padding: `12px 25px`,
    width: 150,
    color: `white`,
    fontWeight: 400,
    fontSize: 16,
    textAlign: `center`,
    cursor: `pointer`,
    border: `1px solid #ddd`,
    borderRadius: 4,
    margin: `14px 0 10px`,
    background: `#fbcdcd`,
});

export const RoundImage = ({ src, className: _className }: { src?: string; className?: string }) => {
    const RIW = styled(`div`)({
        position: `relative`,
    });

    const RIWIMG = styled(`img`)({
        overflow: `hidden`,
        display: `block`,
        height: `auto`,
        width: `100%`,
        borderRadius: `8px`,
    });
    return (
        <RIW>
            <RIWIMG src={src} />
        </RIW>
    );
};

export const Chevron = styled(`span`)<{ top?: string | number }>(({ top }) => ({
    margin: `0 0 0 5px`,
    verticalAlign: `middle`,

    position: `absolute`,
    top,

    "&::before": {
        borderStyle: `solid`,
        borderWidth: `2px 2px 0 0`,
        verticalAlign: `middle`,
        content: `""`,
        display: `inline-block`,
        height: 8,
        left: 4,
        position: `relative`,
        top: 4,
        transform: `rotate(135deg)`,
        width: 8,
    },
}));

const RIW = styled("div")({
    position: "relative",
    width: "100%",
    height: "auto",
    margin: "40px auto 14px",
});

const TxtBox = styled("span")({
    position: "absolute",
    background: "transparent",
    top: 0,
    left: 0,
    padding: 10,
});

const RIWIMG = styled("img")({
    width: "100%",
    height: "auto",
});

export const Carousel = ({ src, text }: { src: string; text?: React.ReactNode }) => (
    <RIW>
        <TxtBox>{text}</TxtBox>
        <RIWIMG src={src} alt="" />
    </RIW>
);

const RIWCL = styled("div")<{ logoPrimaryColor?: string; doubleHigh?: boolean | string }>(
    ({ logoPrimaryColor, doubleHigh }) => ({
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: logoPrimaryColor || doubleHigh ? undefined : "2px solid #eee",
        borderRadius: 8,
        height: 100,
        position: doubleHigh ? "absolute" : "relative",
        bottom: doubleHigh ? 66 : undefined,
        backgroundColor: logoPrimaryColor && !doubleHigh ? `#${logoPrimaryColor}` : undefined,
        background:
            logoPrimaryColor && doubleHigh ? "linear-gradient(to top, rgba(0, 0, 0, .6), rgba(0, 0, 0, 0))" : undefined,
        backgroundImage:
            logoPrimaryColor && !doubleHigh ? "linear-gradient(135deg, #ffffff3b, transparent)" : undefined,
    })
);

const RIWIMGCL = styled("img")({
    verticalAlign: "middle",
    maxWidth: "70%",
    maxHeight: "60%",
});

export const CorpLogo = ({
    src,
    logoPrimaryColor,
    doubleHigh,
}: {
    src?: string;
    logoPrimaryColor?: string;
    doubleHigh?: boolean | string;
    size?: string;
}) => (
    <RIWCL logoPrimaryColor={logoPrimaryColor} doubleHigh={doubleHigh}>
        <RIWIMGCL src={src} alt="" />
    </RIWCL>
);

export const CTA = ({
    onClick,
    cta,
}: {
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    cta: React.ReactNode;
}) => <Button onClick={onClick}>{cta}</Button>;

export const H1 = styled("div")({
    fontSize: 40,
    fontWeight: 700,
    margin: "40px 0 56px 20px",
    textAlign: "left",

    "&.center": {
        textAlign: "center",
    },
});

export const H2 = styled("div")({
    fontSize: 22,
    fontWeight: 400,
    minHeight: 22,
    marginBottom: 10,
    textAlign: "left",

    "&.center": {
        textAlign: "center",
    },

    "&.edit": {
        cursor: `url(${EditImg}), auto`,
    },

    "&.editing": {
        cursor: "text",
    },
});

const Img = styled("img")({
    maxWidth: 400,
    height: "auto",
    margin: "0 auto",
    display: "block",
});

const BtnWrap = styled("div")({
    padding: 20,
    textAlign: "center",
});

const FileLabel = styled("label")({
    border: "1px solid #ccc",
    display: "inline-block",
    padding: "6px 12px",
    cursor: "pointer",
    margin: 20,

    "& input": {
        display: "none",
    },
});

export const ImageUploadModal = ({
    nolink,
    fileLink,
    context,
    clearModal,
    link,
    setLink,
    sendImage,
    setImage,
}: {
    nolink?: boolean;
    fileLink?: string;
    context?: object & { link?: string };
    clearModal: React.MouseEventHandler<HTMLButtonElement> | (() => void);
    link: string;
    setLink: (value: string) => void;
    sendImage: React.MouseEventHandler<HTMLButtonElement> | (() => void);
    setImage: React.ChangeEventHandler<HTMLInputElement>;
}) => (
    <>
        <FileLabel>
            <input onChange={setImage} title="uploadImage" type="file" />
            <span>Upload an image</span>
        </FileLabel>
        {fileLink && <Img alt="" src={fileLink} />}
        {!nolink && (
            <>
                <p>&nbsp;</p>
                <div>Current link: {context?.link}</div>
                <TextField
                    fullWidth
                    variant="standard"
                    label="New link"
                    onChange={v => setLink(v.target.value)}
                    value={link}
                />
            </>
        )}
        <p>&nbsp;</p>
        <BtnWrap>
            <Button type="submit" onClick={clearModal}>
                Cancel
            </Button>
            &nbsp;&nbsp;
            <Button type="submit" onClick={sendImage}>
                Submit
            </Button>
        </BtnWrap>
    </>
);

export const H3 = styled("div")({
    fontSize: 18,
    fontWeight: 400,
    height: 22,
    marginBottom: 10,
    textAlign: "left",

    "&.center": {
        textAlign: "center",
    },

    "&.nomargin": {
        margin: 0,
    },

    "&.edit": {
        cursor: `url(${EditImg}), auto`,
    },

    "&.editing": {
        cursor: "text",
    },

    "&.spacetop": {
        marginTop: 10,
    },
});

export const H4 = styled("div")({
    fontSize: 14,
    fontWeight: 400,
    height: 22,
    marginBottom: 10,
    textAlign: "left",

    "&.center": {
        textAlign: "center",
    },

    "&.nomargin": {
        margin: 0,
    },
});

export const C1 = styled("div")({
    fontSize: 18,
    marginBottom: 10,
    fontWeight: 400,
    letterSpacing: "0em",
    textAlign: "left",

    "&.center": {
        textAlign: "center",
    },
});

export const C2 = styled("div")({
    fontSize: 16,
    marginBottom: 10,
    fontWeight: 400,
    letterSpacing: "0em",
    textAlign: "left",
    margin: "0 auto",
    minHeight: 20,

    "&.edit": {
        cursor: `url(${EditImg}), auto`,
    },

    "&.editing": {
        cursor: "text",
    },

    "&.center": {
        textAlign: "center",
    },

    "&.capitalize": {
        fontWeight: 400,
        textTransform: "uppercase",
    },
});

export const S1 = styled("div")({
    fontSize: 14,
    marginBottom: 10,
    fontWeight: 400,
    color: "#9e9e9e",
    letterSpacing: "0em",
    textAlign: "left",

    "&.center": {
        textAlign: "center",
    },
});
